/**
 * Edit / Delete permission rules for a file resource.
 *
 *   canEditFile / canDeleteFile is true when ANY of:
 *     - viewer is the file owner (`file.user.mine`).
 *     - viewer is a business_admin AND the file owner is a business_admin in
 *       the same company (resolved via `company_id` or `company.id`).
 *     - the file is private AND viewer is a business_admin. The current
 *       payload doesn't always carry company info on tradie owners; we trust
 *       the backend's read scoping (private files are only returned to BAs in
 *       the owning company), so any BA viewing a private file may manage it.
 *
 *   canDeleteFile additionally requires `file.deletable === true` UNLESS the
 *   private-BA override applies (BAs can clean up their company's private
 *   uploads even when the backend marks them non-deletable).
 *
 * Implementation notes
 *   - Owner is read from `file.user`, falling back to `file.file.user`.
 */

export function getFileOwnerUser(file) {
  if (!file) return null
  return file.user || (file.file && file.file.user) || null
}

export function getFileOwnerUserType(file) {
  const u = getFileOwnerUser(file)
  return (u && u.user_type) || null
}

export function isTradieOwnedFile(file) {
  return getFileOwnerUserType(file) === 'tradie'
}

/**
 * RBAC key for a business admin acting on a file.
 * Tradie-owned files use `tradies.files.*`; company library files use `files.*`.
 */
export function businessAdminFilePermissionKey(file, action) {
  const tradieFileActions = new Set([
    'view',
    'create',
    'edit',
    'delete',
    'download',
  ])
  if (isTradieOwnedFile(file) && tradieFileActions.has(action)) {
    return `tradies.files.${action}`
  }
  return `files.${action}`
}

export function hasBusinessAdminFilePermission(
  file,
  user,
  hasPermission,
  action,
) {
  if (!user || user.user_type !== 'business_admin') return true
  if (hasPermission(businessAdminFilePermissionKey(file, action))) return true
  // Also accept the project-context equivalent for tradie-owned files so that
  // BAs with only project-scoped permissions can edit/download from ViewFilePage.
  const projectFileActions = new Set([
    'view',
    'create',
    'edit',
    'delete',
    'download',
  ])
  if (isTradieOwnedFile(file) && projectFileActions.has(action)) {
    return hasPermission(`projects.tradie.files.${action}`)
  }
  return false
}

export function resolveUserCompanyId(u) {
  if (!u) return null
  let raw = null
  if (u.company_id != null && u.company_id !== '') {
    raw = u.company_id
  } else if (u.company && u.company.id != null) {
    raw = u.company.id
  }
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function isSameCompanyAsFileOwner(file, user) {
  if (!user) return false
  const mine = resolveUserCompanyId(user)
  const theirs = resolveUserCompanyId(getFileOwnerUser(file))
  if (mine == null || theirs == null) return false
  return mine === theirs
}

/**
 * BA viewer + private file → company-internal file that any BA on the team
 * should be able to manage.
 *
 * The owner can be a BA (company-internal upload) or a tradie (private file
 * uploaded for that tradie). The current file payload doesn't always carry the
 * tradie's `company_id` or the creator's company, so we can't always verify
 * same-company on the client. Instead we trust the backend's read scoping:
 * if the API returned a private tradie file to a BA at all, that BA already
 * belongs to the company that owns it.
 */
export function isPrivateBaFileForCoBa(file, user) {
  if (!file || !user) return false
  if (file.visibility !== 'private') return false
  if (user.user_type !== 'business_admin') return false
  return true
}

export function canEditFile(file, user) {
  if (!file || !user) return false

  const owner = getFileOwnerUser(file)
  if (owner && owner.mine) return true

  if (isPrivateBaFileForCoBa(file, user)) return true

  // BA viewing a tradie's public file (shared with their company) can edit it
  // in place — e.g. swapping in a renewed licence the tradie sent directly —
  // instead of uploading a duplicate. Delete stays gated separately via
  // canDeleteFile, which this does not affect.
  if (
    user.user_type === 'business_admin' &&
    isTradieOwnedFile(file) &&
    file.visibility === 'public'
  ) {
    return true
  }

  return false
}

/**
 * Combined edit gate used by file action UI: RBAC permission, the backend's
 * explicit `editable` override, then the ownership rules above.
 *
 * For a tradie's own file, edit rides on the same `tradies.files.view` /
 * `projects.tradie.files.view` grant as viewing it — every business admin
 * associated with the tradie's company who can see the file can edit it in
 * place (e.g. swapping in a renewed licence the tradie sent directly),
 * rather than needing a separate, narrower edit permission. Delete stays
 * gated behind its own stricter permission in canDeleteFile.
 */
export function canViewerEditFile(file, user, hasPermission) {
  const permitted =
    isTradieOwnedFile(file) && user && user.user_type === 'business_admin'
      ? hasBusinessAdminFilePermission(file, user, hasPermission, 'view')
      : hasBusinessAdminFilePermission(file, user, hasPermission, 'edit')
  if (!permitted) return false
  if (
    user &&
    user.user_type === 'business_admin' &&
    file &&
    file.editable === false
  ) {
    return false
  }
  return canEditFile(file, user)
}

export function canDeleteFile(file, user) {
  if (!file || !user) return false

  if (isPrivateBaFileForCoBa(file, user)) return true

  if (!file.deletable) return false

  const owner = getFileOwnerUser(file)
  if (owner && owner.mine) return true

  const viewerIsBa = user.user_type === 'business_admin'
  const ownerIsBa = getFileOwnerUserType(file) === 'business_admin'
  if (viewerIsBa && ownerIsBa && isSameCompanyAsFileOwner(file, user)) {
    return true
  }

  return false
}
